import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Input } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';
import DeleteConfirmModal from '../../components/ui/DeleteConfirmModal';
import { useToast } from '../../components/ui/Toast';
import SafeIcon from '../../common/SafeIcon';
import UserPickerDropdown from '../../components/Settings/UserPickerDropdown';
import {
  getActiveDirectoryUsers,
  resolveDisplayName,
  useUsersDirectory,
} from '../../lib/usersDirectory';
import {
  canAdminPmInvites,
  PM_ADMIN_BYPASS_ROLES,
  slugify,
} from '../../lib/pmEventUtils';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiTrash2, FiCopy, FiEdit2 } = FiIcons;

/** Admin / Super Admin only — stricter than canAdminPmInvites (which includes Manager). */
function canReassignPmInvites(user) {
  if (!user?.role) return false;
  return PM_ADMIN_BYPASS_ROLES.includes(user.role);
}

function inviteOwnerLabel(invite, agencyUsers = []) {
  if (invite?.placeholder_owner_name?.trim()) {
    const title = invite.placeholder_owner_title?.trim();
    return title
      ? `${invite.placeholder_owner_name.trim()} (${title})`
      : invite.placeholder_owner_name.trim();
  }
  const resolved = resolveDisplayName(invite?.user_id, invite?.user);
  if (resolved) return resolved;
  if (invite?.user_id) {
    const u = agencyUsers.find((row) => row.id === invite.user_id);
    if (u?.full_name?.trim()) return u.full_name.trim();
  }
  if (invite?.mentors_user?.full_name?.trim()) return invite.mentors_user.full_name.trim();
  if (invite?.mentors_user_id) return 'Student invite';
  return 'Unknown owner';
}

function inviteDeleteLabel(invite, agencyUsers = []) {
  const slug = invite?.slug ? `/${invite.slug}` : 'this invitation link';
  return `${slug} (owner: ${inviteOwnerLabel(invite, agencyUsers)})`;
}

const SLUG_FORMAT_HINT =
  'Lowercase letters, numbers, and hyphens only (1–50 chars, no leading/trailing hyphen).';

function formatInviteDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function normalizeInviteSlugInput(raw) {
  return String(raw || '')
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .slice(0, 50);
}

function suggestInviteSlug(baseText, existingSlugs) {
  const taken = new Set((existingSlugs || []).map((s) => String(s || '').toLowerCase()));
  let base = slugify(baseText || 'invite').slice(0, 50);
  if (!base) base = 'invite';
  base = base.replace(/-+$/g, '') || 'invite';
  if (!api.agentPublicPages.isValidSlug(base)) {
    base = (base.replace(/[^a-z0-9-]/g, '').replace(/^-+|-+$/g, '') || 'invite').slice(0, 50);
    if (!api.agentPublicPages.isValidSlug(base)) base = 'invite';
  }
  if (!taken.has(base)) return base;
  for (let n = 2; n < 100; n += 1) {
    const suffix = `-${n}`;
    const candidate = `${base.slice(0, Math.max(1, 50 - suffix.length))}${suffix}`;
    if (api.agentPublicPages.isValidSlug(candidate) && !taken.has(candidate)) return candidate;
  }
  return `${base.slice(0, 40)}-${Date.now().toString(36)}`.slice(0, 50);
}

function OwnerTypeToggle({ value, onChange, disabled, name = 'invite-owner-type' }) {
  return (
    <div className="flex flex-wrap gap-4 text-sm text-gray-700">
      <label className="inline-flex items-center gap-2">
        <input
          type="radio"
          name={name}
          checked={value === 'crm'}
          onChange={() => onChange('crm')}
          disabled={disabled}
        />
        CRM user
      </label>
      <label className="inline-flex items-center gap-2">
        <input
          type="radio"
          name={name}
          checked={value === 'placeholder'}
          onChange={() => onChange('placeholder')}
          disabled={disabled}
        />
        Not in the system yet
      </label>
    </div>
  );
}

function ShowInviterCardCheckbox({ checked, onChange, disabled }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
      <input
        type="checkbox"
        className="rounded border-gray-300"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      Show &quot;Invited By&quot; card on landing page
    </label>
  );
}

function InviteRow({
  invite,
  url,
  agencyUsers,
  canDelete,
  canEdit,
  onCopy,
  onRemove,
  onEdit,
}) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <div>
        <div className="font-medium text-gray-900 font-mono">/{invite.slug}</div>
        <div className="text-xs text-gray-600">
          {inviteOwnerLabel(invite, agencyUsers)}
          {invite.placeholder_owner_name ? (
            <span className="text-gray-400"> · placeholder</span>
          ) : null}
          {invite.show_inviter_card === false ? (
            <span className="text-gray-400"> · Invited By hidden</span>
          ) : null}
        </div>
        <div className="text-xs text-gray-500 break-all">
          {url}
        </div>
        <div className="text-xs text-gray-500">
          {invite.click_count ?? 0} clicks
          {invite.created_at ? ` · created ${formatInviteDate(invite.created_at)}` : ''}
        </div>
      </div>
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => onCopy(invite)}
          className="p-2 text-gray-400 hover:text-primary"
          title="Copy URL"
        >
          <SafeIcon icon={FiCopy} className="w-4 h-4" />
        </button>
        {canEdit && (
          <button
            type="button"
            onClick={() => onEdit(invite)}
            className="p-2 text-gray-400 hover:text-primary"
            title="Edit / reassign"
          >
            <SafeIcon icon={FiEdit2} className="w-4 h-4" />
          </button>
        )}
        {canDelete && (
          <button
            type="button"
            onClick={() => onRemove(invite)}
            className="p-2 text-gray-400 hover:text-red-600"
            title="Delete"
          >
            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Invitation links manager for a single Mentors event.
 * Inline accordion on the events list (single source of truth — not duplicated in the editor).
 */
export default function EventInvitationLinksPanel({
  eventId,
  eventSlug,
  customDomain = null,
  agencyId: agencyIdProp = null,
}) {
  const { user } = useAuth();
  const { addToast } = useToast();
  const canAdminInvites = canAdminPmInvites(user);
  const canReassign = canReassignPmInvites(user);
  const agencyId = agencyIdProp || user?.agency_id;

  const [loading, setLoading] = useState(true);
  const [invites, setInvites] = useState([]);
  const { users: directoryUsers } = useUsersDirectory();
  const agencyUsers = useMemo(() => getActiveDirectoryUsers(directoryUsers), [directoryUsers]);
  const [lookupUserId, setLookupUserId] = useState(null);
  const [creatingInviteFor, setCreatingInviteFor] = useState(null);
  const [createFormFor, setCreateFormFor] = useState(null); // user id | 'placeholder'
  const [ownerType, setOwnerType] = useState('crm'); // crm | placeholder
  const [draftSlug, setDraftSlug] = useState('');
  const [draftPlaceholderName, setDraftPlaceholderName] = useState('');
  const [draftPlaceholderTitle, setDraftPlaceholderTitle] = useState('');
  const [draftShowInviterCard, setDraftShowInviterCard] = useState(true);
  const [slugError, setSlugError] = useState(null);
  const [agentPublicSlugs, setAgentPublicSlugs] = useState({}); // userId -> slug
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editOwnerType, setEditOwnerType] = useState('crm');
  const [editUserId, setEditUserId] = useState(null);
  const [editPlaceholderName, setEditPlaceholderName] = useState('');
  const [editPlaceholderTitle, setEditPlaceholderTitle] = useState('');
  const [editShowInviterCard, setEditShowInviterCard] = useState(true);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Staff + placeholder links (exclude student-owned mentors invites from CRM panel).
  const staffInvites = useMemo(
    () => (invites ?? []).filter((inv) => inv.user_id || inv.placeholder_owner_name),
    [invites],
  );

  const myInvites = useMemo(
    () => staffInvites.filter((inv) => inv.user_id === user?.id),
    [staffInvites, user?.id],
  );

  const lookupInvites = useMemo(
    () => (lookupUserId ? staffInvites.filter((inv) => inv.user_id === lookupUserId) : []),
    [staffInvites, lookupUserId],
  );

  const placeholderInvites = useMemo(
    () => staffInvites.filter((inv) => inv.placeholder_owner_name),
    [staffInvites],
  );

  const visibleInvites = useMemo(() => {
    if (canAdminInvites) return staffInvites;
    return staffInvites.filter((inv) => inv.user_id === user?.id);
  }, [canAdminInvites, staffInvites, user?.id]);

  const existingSlugs = useMemo(
    () => staffInvites.map((inv) => inv.slug).filter(Boolean),
    [staffInvites],
  );

  const eventForUrls = useMemo(
    () => ({ slug: eventSlug, custom_domain: customDomain }),
    [eventSlug, customDomain],
  );
  const inviteDisplayUrl = useCallback(
    (invite) => {
      const { primaryUrl, customUrl } = api.pipelineEvents.buildInviteUrls(eventForUrls, invite);
      return customUrl || primaryUrl;
    },
    [eventForUrls],
  );

  const loadInvites = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const data = await api.pmEvents.getById(eventId);
      setInvites(data?.invitation_links ?? []);
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to load invitation links', message: err.message });
    } finally {
      setLoading(false);
    }
  }, [eventId, addToast]);

  useEffect(() => {
    loadInvites();
  }, [loadInvites]);

  useEffect(() => {
    if (!user?.id) return;
    api.agentPublicPages
      .getMine()
      .then((page) => {
        if (page?.slug) {
          setAgentPublicSlugs((prev) => ({ ...prev, [user.id]: page.slug }));
        }
      })
      .catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    if (!lookupUserId || !canAdminInvites) return;
    if (agentPublicSlugs[lookupUserId]) return;
    let cancelled = false;
    api.agentPublicPages
      .getByUserId(lookupUserId)
      .then((page) => {
        if (!cancelled && page?.slug) {
          setAgentPublicSlugs((prev) => ({ ...prev, [lookupUserId]: page.slug }));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [lookupUserId, canAdminInvites, agentPublicSlugs]);

  const suggestionBaseForUser = (targetUserId) => {
    const profileSlug = agentPublicSlugs[targetUserId];
    if (profileSlug) return profileSlug;
    const u = agencyUsers.find((row) => row.id === targetUserId);
    return resolveDisplayName(targetUserId, u) || user?.full_name || 'invite';
  };

  const resetCreateDraft = () => {
    setDraftSlug('');
    setDraftPlaceholderName('');
    setDraftPlaceholderTitle('');
    setDraftShowInviterCard(true);
    setSlugError(null);
    setOwnerType('crm');
  };

  const openCreateForm = (targetUserId, preferredOwnerType = 'crm') => {
    const nextType = canAdminInvites ? preferredOwnerType : 'crm';
    setOwnerType(nextType);
    if (nextType === 'placeholder') {
      setCreateFormFor('placeholder');
      setDraftSlug(suggestInviteSlug('invite', existingSlugs));
    } else {
      if (!targetUserId) return;
      setCreateFormFor(targetUserId);
      setDraftSlug(suggestInviteSlug(suggestionBaseForUser(targetUserId), existingSlugs));
    }
    setDraftPlaceholderName('');
    setDraftPlaceholderTitle('');
    setDraftShowInviterCard(true);
    setSlugError(null);
  };

  const closeCreateForm = () => {
    setCreateFormFor(null);
    resetCreateDraft();
  };

  const onOwnerTypeChange = (next) => {
    setOwnerType(next);
    if (next === 'placeholder') {
      setCreateFormFor('placeholder');
      if (!draftSlug) {
        setDraftSlug(suggestInviteSlug('invite', existingSlugs));
      }
    } else if (createFormFor === 'placeholder') {
      const fallback = lookupUserId || user?.id;
      setCreateFormFor(fallback);
      setDraftSlug(suggestInviteSlug(suggestionBaseForUser(fallback), existingSlugs));
    }
  };

  const onDraftSlugChange = (raw) => {
    const next = normalizeInviteSlugInput(raw);
    setDraftSlug(next);
    if (!next) {
      setSlugError('Invitation slug is required');
      return;
    }
    if (!api.agentPublicPages.isValidSlug(next)) {
      setSlugError(SLUG_FORMAT_HINT);
      return;
    }
    if (existingSlugs.some((s) => String(s).toLowerCase() === next)) {
      setSlugError('That invite slug is already used for this event. Please choose another.');
      return;
    }
    setSlugError(null);
  };

  const createInvite = async (targetUserId) => {
    const slug = draftSlug.trim().toLowerCase();
    if (!slug) {
      setSlugError('Invitation slug is required');
      return;
    }
    if (!api.agentPublicPages.isValidSlug(slug)) {
      setSlugError(SLUG_FORMAT_HINT);
      return;
    }
    if (existingSlugs.some((s) => String(s).toLowerCase() === slug)) {
      setSlugError('That invite slug is already used for this event. Please choose another.');
      return;
    }

    const isPlaceholder = canAdminInvites && ownerType === 'placeholder';
    if (isPlaceholder && !draftPlaceholderName.trim()) {
      addToast({ type: 'error', title: 'Name is required for placeholder owners' });
      return;
    }
    if (!isPlaceholder && !targetUserId) return;

    const busyKey = isPlaceholder ? 'placeholder' : targetUserId;
    setCreatingInviteFor(busyKey);
    setSlugError(null);
    try {
      const payload = {
        event_id: eventId,
        agency_id: agencyId,
        slug,
        show_inviter_card: draftShowInviterCard,
      };
      if (isPlaceholder) {
        payload.placeholder_owner_name = draftPlaceholderName.trim();
        payload.placeholder_owner_title = draftPlaceholderTitle.trim() || null;
        payload.user_id = null;
      } else {
        payload.user_id = targetUserId;
      }
      const row = await api.pmEventInvitationLinks.create(payload);
      setInvites((list) => [row, ...list.filter((x) => x.id !== row.id)]);
      addToast({ type: 'success', title: 'Invitation link created' });
      closeCreateForm();
    } catch (err) {
      const message = err?.message || 'Could not create invite';
      if (/already used for this event|already taken|duplicate/i.test(message)) {
        setSlugError(message);
      } else if (/Slug must be|slug is required/i.test(message)) {
        setSlugError(message);
      } else {
        addToast({ type: 'error', title: 'Could not create invite', message });
      }
    } finally {
      setCreatingInviteFor(null);
    }
  };

  const openEditForm = (invite) => {
    if (!invite?.id || !canReassign) return;
    setEditTarget(invite);
    if (invite.placeholder_owner_name) {
      setEditOwnerType('placeholder');
      setEditUserId(null);
      setEditPlaceholderName(invite.placeholder_owner_name || '');
      setEditPlaceholderTitle(invite.placeholder_owner_title || '');
    } else {
      setEditOwnerType('crm');
      setEditUserId(invite.user_id || null);
      setEditPlaceholderName('');
      setEditPlaceholderTitle('');
    }
    setEditShowInviterCard(invite.show_inviter_card !== false);
  };

  const closeEditForm = () => {
    if (isSavingEdit) return;
    setEditTarget(null);
  };

  const saveEdit = async () => {
    if (!editTarget?.id || !canReassign) return;
    if (editOwnerType === 'crm' && !editUserId) {
      addToast({ type: 'error', title: 'Select a CRM user' });
      return;
    }
    if (editOwnerType === 'placeholder' && !editPlaceholderName.trim()) {
      addToast({ type: 'error', title: 'Name is required for placeholder owners' });
      return;
    }

    setIsSavingEdit(true);
    try {
      const updates =
        editOwnerType === 'placeholder'
          ? {
              user_id: null,
              mentors_user_id: null,
              placeholder_owner_name: editPlaceholderName.trim(),
              placeholder_owner_title: editPlaceholderTitle.trim() || null,
              show_inviter_card: editShowInviterCard,
            }
          : {
              user_id: editUserId,
              mentors_user_id: null,
              placeholder_owner_name: null,
              placeholder_owner_title: null,
              show_inviter_card: editShowInviterCard,
            };
      const row = await api.pmEventInvitationLinks.update(editTarget.id, updates);
      setInvites((list) => list.map((x) => (x.id === row.id ? row : x)));
      addToast({ type: 'success', title: 'Invitation link updated' });
      setEditTarget(null);
    } catch (err) {
      addToast({ type: 'error', title: 'Could not update invite', message: err.message });
    } finally {
      setIsSavingEdit(false);
    }
  };

  const requestRemoveInvite = (invite) => {
    if (!invite?.id) return;
    setDeleteTarget(invite);
  };

  const confirmRemoveInvite = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleting(true);
    try {
      await api.pmEventInvitationLinks.delete(deleteTarget.id);
      setInvites((list) => list.filter((x) => x.id !== deleteTarget.id));
      setDeleteTarget(null);
      addToast({ type: 'success', title: 'Invitation link deleted' });
    } catch (err) {
      addToast({ type: 'error', title: 'Could not delete invite', message: err.message });
    } finally {
      setIsDeleting(false);
    }
  };

  const copyInviteUrl = async (invite) => {
    const url = inviteDisplayUrl(invite);
    try {
      await navigator.clipboard.writeText(url);
      addToast({ type: 'success', title: 'Invite URL copied' });
    } catch {
      addToast({ type: 'error', title: 'Could not copy' });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner size="md" />
      </div>
    );
  }

  const lookupUser = lookupUserId ? agencyUsers.find((u) => u.id === lookupUserId) : null;
  const createDisabled =
    Boolean(slugError)
    || !draftSlug.trim()
    || !api.agentPublicPages.isValidSlug(draftSlug.trim().toLowerCase())
    || (ownerType === 'placeholder' && !draftPlaceholderName.trim());

  const renderCreateForm = (targetUserId) => {
    const isPlaceholder = canAdminInvites && ownerType === 'placeholder';
    const busy = isPlaceholder
      ? creatingInviteFor === 'placeholder'
      : creatingInviteFor === targetUserId;

    return (
      <div className="space-y-3 rounded-md border border-gray-200 bg-white p-3">
        {canAdminInvites && (
          <OwnerTypeToggle
            name="invite-create-owner-type"
            value={ownerType}
            onChange={onOwnerTypeChange}
            disabled={Boolean(busy)}
          />
        )}
        {isPlaceholder ? (
          <>
            <Input
              label="Name"
              value={draftPlaceholderName}
              onChange={(e) => setDraftPlaceholderName(e.target.value)}
              placeholder="e.g. Jordan Lee"
              disabled={busy}
            />
            <Input
              label="Title (optional)"
              value={draftPlaceholderTitle}
              onChange={(e) => setDraftPlaceholderTitle(e.target.value)}
              placeholder="e.g. Agency Owner"
              disabled={busy}
            />
          </>
        ) : null}
        <Input
          label="Invite slug"
          value={draftSlug}
          onChange={(e) => onDraftSlugChange(e.target.value)}
          error={slugError || undefined}
          placeholder="your-invite-slug"
          autoComplete="off"
          spellCheck={false}
          disabled={busy}
        />
        <p className="text-xs text-gray-500">
          Live URL preview:{' '}
          <span className="font-mono break-all text-gray-700">
            {inviteDisplayUrl({ slug: draftSlug || 'slug' }) || '—'}
          </span>
        </p>
        {!slugError && (
          <p className="text-xs text-gray-400">{SLUG_FORMAT_HINT}</p>
        )}
        <ShowInviterCardCheckbox
          checked={draftShowInviterCard}
          onChange={setDraftShowInviterCard}
          disabled={busy}
        />
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            isLoading={busy}
            disabled={createDisabled || busy}
            onClick={() => createInvite(isPlaceholder ? null : targetUserId)}
          >
            Create link
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={closeCreateForm}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  const renderEditForm = () => {
    if (!editTarget) return null;
    return (
      <div className="space-y-3 rounded-md border border-primary/30 bg-white p-4">
        <h3 className="text-sm font-medium text-gray-900">
          Edit / reassign
          {' '}
          <span className="font-mono text-gray-600">/{editTarget.slug}</span>
        </h3>
        <OwnerTypeToggle
          name="invite-edit-owner-type"
          value={editOwnerType}
          onChange={setEditOwnerType}
          disabled={isSavingEdit}
        />
        {editOwnerType === 'crm' ? (
          <UserPickerDropdown
            label="CRM user"
            source="directory"
            value={editUserId}
            onChange={setEditUserId}
            placeholder="Search by name…"
          />
        ) : (
          <>
            <Input
              label="Name"
              value={editPlaceholderName}
              onChange={(e) => setEditPlaceholderName(e.target.value)}
              placeholder="e.g. Jordan Lee"
              disabled={isSavingEdit}
            />
            <Input
              label="Title (optional)"
              value={editPlaceholderTitle}
              onChange={(e) => setEditPlaceholderTitle(e.target.value)}
              placeholder="e.g. Agency Owner"
              disabled={isSavingEdit}
            />
          </>
        )}
        <ShowInviterCardCheckbox
          checked={editShowInviterCard}
          onChange={setEditShowInviterCard}
          disabled={isSavingEdit}
        />
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            isLoading={isSavingEdit}
            disabled={isSavingEdit}
            onClick={saveEdit}
          >
            Save changes
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isSavingEdit}
            onClick={closeEditForm}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {editTarget && canReassign ? renderEditForm() : null}

      <div className="space-y-3 rounded-md border border-gray-100 bg-gray-50/60 p-4">
        <h3 className="text-sm font-medium text-gray-900">My invitation links</h3>
        {myInvites.length > 0 ? (
          <ul className="space-y-3">
            {myInvites.map((inv) => (
              <li key={inv.id}>
                <InviteRow
                  invite={inv}
                  url={inviteDisplayUrl(inv)}
                  agencyUsers={agencyUsers}
                  canDelete={canAdminInvites || inv.user_id === user?.id}
                  canEdit={canReassign}
                  onCopy={copyInviteUrl}
                  onRemove={requestRemoveInvite}
                  onEdit={openEditForm}
                />
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-600">You don’t have an invitation link for this event yet.</p>
        )}

        {createFormFor === user?.id && ownerType === 'crm' ? (
          renderCreateForm(user.id)
        ) : createFormFor === 'placeholder' && !lookupUserId ? (
          renderCreateForm(null)
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => openCreateForm(user.id, 'crm')}
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
              {myInvites.length > 0 ? 'Add another invitation link' : 'Create my link'}
            </Button>
            {canAdminInvites && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => openCreateForm(null, 'placeholder')}
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
                Create for someone not in the system
              </Button>
            )}
          </div>
        )}
      </div>

      {canAdminInvites && (
        <div className="space-y-3 rounded-md border border-gray-100 p-4">
          <h3 className="text-sm font-medium text-gray-900">Look up another user</h3>
          <p className="text-xs text-gray-500">
            Managers and admins can view or create invitation links on behalf of others.
            Reassigning an existing link’s owner is Admin / Super Admin only.
          </p>
          <UserPickerDropdown
            label="User"
            source="directory"
            users={agencyUsers.filter((u) => u.id !== user?.id)}
            value={lookupUserId}
            onChange={(id) => {
              setLookupUserId(id);
              if (createFormFor && createFormFor !== user?.id && createFormFor !== 'placeholder') {
                closeCreateForm();
              }
            }}
            placeholder="Search by name…"
          />
          {lookupUserId && (
            <div className="text-sm space-y-3 pt-1">
              <div className="font-medium text-gray-800">
                {resolveDisplayName(lookupUserId, lookupUser) || 'Selected user'}
              </div>
              {lookupInvites.length > 0 ? (
                <ul className="space-y-3">
                  {lookupInvites.map((inv) => (
                    <li key={inv.id}>
                      <InviteRow
                        invite={inv}
                        url={inviteDisplayUrl(inv)}
                        agencyUsers={agencyUsers}
                        canDelete
                        canEdit={canReassign}
                        onCopy={copyInviteUrl}
                        onRemove={requestRemoveInvite}
                        onEdit={openEditForm}
                      />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-600">No invitation links for this user yet.</p>
              )}
              {createFormFor === lookupUserId || (createFormFor === 'placeholder' && lookupUserId) ? (
                renderCreateForm(lookupUserId)
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => openCreateForm(lookupUserId, 'crm')}
                >
                  <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
                  {lookupInvites.length > 0 ? 'Add another invitation link' : 'Create link for this user'}
                </Button>
              )}
            </div>
          )}

          {placeholderInvites.length > 0 && (
            <div className="space-y-2 pt-3 border-t border-gray-100">
              <h4 className="text-sm font-medium text-gray-800">Placeholder owners</h4>
              <ul className="space-y-3">
                {placeholderInvites.map((inv) => (
                  <li key={inv.id}>
                    <InviteRow
                      invite={inv}
                      url={inviteDisplayUrl(inv)}
                      agencyUsers={agencyUsers}
                      canDelete
                      canEdit={canReassign}
                      onCopy={copyInviteUrl}
                      onRemove={requestRemoveInvite}
                      onEdit={openEditForm}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="space-y-2 pt-2 border-t border-gray-100">
        <h3 className="text-sm font-medium text-gray-900">
          {canAdminInvites ? 'All invitation links' : 'Your invitation links'}
        </h3>
        {visibleInvites.length === 0 ? (
          <p className="text-sm text-gray-500">No invitation links yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {visibleInvites.map((inv) => (
              <li key={inv.id} className="py-2">
                <InviteRow
                  invite={inv}
                  url={inviteDisplayUrl(inv)}
                  agencyUsers={agencyUsers}
                  canDelete={canAdminInvites || inv.user_id === user?.id}
                  canEdit={canReassign}
                  onCopy={copyInviteUrl}
                  onRemove={requestRemoveInvite}
                  onEdit={openEditForm}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      <DeleteConfirmModal
        isOpen={Boolean(deleteTarget)}
        onConfirm={confirmRemoveInvite}
        onCancel={() => {
          if (!isDeleting) setDeleteTarget(null);
        }}
        leadName={deleteTarget ? inviteDeleteLabel(deleteTarget, agencyUsers) : ''}
        isDeleting={isDeleting}
      />
    </div>
  );
}
